import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { NotificationContext } from "cerulean/src/NotificationContext/NotificationContext";

type DraftFile = {
  name: string;
  file: File;
  previewUrl: string;
};

type FileUploadContextValue = {
  files: DraftFile[];
  isDragActive: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (file: DraftFile) => void;
  reset: () => void;
  handleDragEvent: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
};

const FileUploadContext = createContext<FileUploadContextValue | null>(null);

export function FileUploadProvider({
  children,
  accept,
}: {
  children: React.ReactNode;
  accept?: string;
}) {
  const { sendNotification } = useContext(NotificationContext);

  const [files, setFiles] = useState<DraftFile[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set());

  const isAcceptedFileType = useCallback(
    (f: File): boolean => {
      if (!accept) return true;
      const acceptedTypes = accept.split(",").map((t) => t.trim().toLowerCase());
      const fileName = f.name.toLowerCase();
      const fileType = f.type.toLowerCase();
      return acceptedTypes.some((accepted) => {
        if (accepted.startsWith(".")) return fileName.endsWith(accepted);
        if (accepted.endsWith("/*")) return fileType.startsWith(accepted.slice(0, -2));
        return fileType === accepted;
      });
    },
    [accept]
  );

  // Files are held in memory as object URLs; they are only uploaded to S3 on submit.
  const addFiles = useCallback(
    (fileList: FileList) => {
      const incoming = Array.from(fileList);
      if (!incoming.length) return;

      const accepted = incoming.filter((f) => {
        if (isAcceptedFileType(f)) return true;
        sendNotification({
          type: "negative",
          text: `Invalid file type: ${f.name}`,
        });
        return false;
      });
      if (!accepted.length) return;

      setFiles((prev) => {
        const next = [...prev];
        for (const f of accepted) {
          const previewUrl = URL.createObjectURL(f);
          blobUrlsRef.current.add(previewUrl);
          const existingIndex = next.findIndex((df) => df.name === f.name);
          if (existingIndex >= 0) {
            URL.revokeObjectURL(next[existingIndex].previewUrl);
            blobUrlsRef.current.delete(next[existingIndex].previewUrl);
            next[existingIndex] = { name: f.name, file: f, previewUrl };
          } else {
            next.push({ name: f.name, file: f, previewUrl });
          }
        }
        return next;
      });
    },
    [isAcceptedFileType, sendNotification]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addFiles(e.target.files);
      }
      // Clear the input so re-selecting the same file still fires onChange.
      e.target.value = "";
    },
    [addFiles]
  );

  const removeFile = useCallback((file: DraftFile) => {
    URL.revokeObjectURL(file.previewUrl);
    blobUrlsRef.current.delete(file.previewUrl);
    setFiles((prev) => prev.filter((f) => f.name !== file.name));
  }, []);

  const reset = useCallback(() => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current.clear();
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleDragEvent = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (["dragenter", "dragover"].includes(e.type)) {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (e.dataTransfer.files) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
      blobUrls.clear();
    };
  }, []);

  const value: FileUploadContextValue = {
    files,
    isDragActive,
    fileInputRef,
    handleFileChange,
    removeFile,
    reset,
    handleDragEvent,
    handleDrop,
  };

  return <FileUploadContext.Provider value={value}>{children}</FileUploadContext.Provider>;
}

export function useFileUpload() {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("useFileUpload must be used within a FileUploadProvider");
  }
  return context;
}
